/**
 * Date helpers for the calendar.
 *
 * Two conventions run through all of this. Weeks start on Monday, because the
 * calendar is Russian-facing. And every date the user sees or types is in their
 * local timezone, while everything stored or compared is an ISO UTC string —
 * mixing the two is the classic way to get an event that shows up on the wrong
 * day for half the team.
 */

export const WEEKDAY_LABELS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'] as const;

const MONTH_NAMES = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
] as const;

export function monthTitle(date: Date): string {
  return `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
}

export function startOfDay(date: Date): Date {
  const out = new Date(date);
  out.setHours(0, 0, 0, 0);
  return out;
}

export function endOfDay(date: Date): Date {
  const out = new Date(date);
  out.setHours(23, 59, 59, 999);
  return out;
}

export function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function addMonths(date: Date, delta: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + delta, 1);
}

export function addDays(date: Date, delta: number): Date {
  const out = new Date(date);
  out.setDate(out.getDate() + delta);
  return out;
}

/** Monday-based weekday index, 0..6. */
export function weekdayIndex(date: Date): number {
  return (date.getDay() + 6) % 7;
}

/**
 * The 6x7 grid a month view shows, including the leading and trailing days
 * from the neighbouring months.
 *
 * Always six rows, never five: a grid that changes height between months makes
 * the whole page jump when you page through it.
 */
export function monthGrid(month: Date): Date[] {
  const first = startOfMonth(month);
  const gridStart = addDays(first, -weekdayIndex(first));
  return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function isToday(date: Date): boolean {
  return isSameDay(date, new Date());
}

export function isWeekend(date: Date): boolean {
  return weekdayIndex(date) >= 5;
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

export function formatDayLong(date: Date): string {
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', weekday: 'long' });
}

export function formatDateShort(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

/** "через 2 ч 15 мин" / "5 мин назад", for the upcoming list. */
export function formatRelative(iso: string, now = Date.now()): string {
  const diffMs = new Date(iso).getTime() - now;
  const past = diffMs < 0;
  const minutes = Math.round(Math.abs(diffMs) / 60000);

  let text: string;
  if (minutes < 1) text = 'меньше минуты';
  else if (minutes < 60) text = `${minutes} мин`;
  else if (minutes < 60 * 24) {
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    text = rest === 0 ? `${hours} ч` : `${hours} ч ${rest} мин`;
  } else {
    const days = Math.round(minutes / (60 * 24));
    text = `${days} дн`;
  }
  return past ? `${text} назад` : `через ${text}`;
}

export function formatRelativeDay(date: Date): string {
  const today = startOfDay(new Date());
  const target = startOfDay(date);
  const days = Math.round((target.getTime() - today.getTime()) / 86_400_000);
  if (days === 0) return 'Сегодня';
  if (days === 1) return 'Завтра';
  if (days === -1) return 'Вчера';
  return formatDayLong(date);
}

// ---------------------------------------------------------------------------
// <input type="datetime-local"> / <input type="date"> bridging
// ---------------------------------------------------------------------------

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * Local-time value for a datetime-local input.
 *
 * `toISOString()` cannot be used here: it converts to UTC, so a user in UTC+3
 * would see their 10:00 meeting as 07:00 in the form.
 */
export function toLocalInput(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function toDateInput(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Parses either input format back to an ISO UTC string. */
export function fromLocalInput(value: string): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

/** A sensible default slot on a given day: the next whole hour, one hour long. */
export function defaultSlotOn(day: Date): { start: string; end: string } {
  const now = new Date();
  const base = new Date(day);
  if (isSameDay(day, now)) base.setHours(now.getHours() + 1, 0, 0, 0);
  else base.setHours(10, 0, 0, 0);

  const end = new Date(base);
  end.setHours(end.getHours() + 1);
  return { start: base.toISOString(), end: end.toISOString() };
}

/** Does an event overlap a given local day? */
export function eventOnDay(event: { start: string; end: string }, day: Date): boolean {
  const from = startOfDay(day).getTime();
  const to = endOfDay(day).getTime();
  const start = new Date(event.start).getTime();
  const end = new Date(event.end).getTime();
  return start <= to && end >= from;
}

export const REMINDER_OPTIONS = [
  { value: null, label: 'Без напоминания' },
  { value: 0, label: 'В момент начала' },
  { value: 5, label: 'За 5 минут' },
  { value: 15, label: 'За 15 минут' },
  { value: 30, label: 'За 30 минут' },
  { value: 60, label: 'За час' },
  { value: 60 * 24, label: 'За день' },
] as const;
